import axios from "axios";
import CheckBox from "components/Checkbox";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from "react-router-dom";
import { animated } from "react-spring";
import styled from "styled-components";
import { useUser } from "../../auth";
import Button from "../../components/Button";
import { Flex } from "../../components/Flex";
import { FormInput, Label } from "../../components/FormInput";
import Loader from "../../components/Loader";
import { Panel } from "../../components/Panel";
import { Text } from "../../components/Text";
import rightGfx from "../../img/loginright.jpg";
import logoSrc from "../../img/logo.svg";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors.bg};
  width: 100vw;
  height: 100vh;
  position: fixed;
`;

const LoginPanel = styled(Panel)`
  width: 1000px;
  height: 600px;
  position: relative;
`;

const Image = styled(animated.div)<{ src: string }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 600px;
  height: 600px;
  background-image: url("${(props) => props.src}");
  background-size: cover;
  z-index: 1;
`;

const InfoPanel = styled(animated.div)<{ left: boolean }>`
  position: absolute;
  height: 600px;
  width: 400px;
  left: ${(props) => (props.left ? 0 : 600)}px;
  padding: 18px;
  display: flex;
  flex-direction: column;
`;

const Logo = styled.img`
  width: 60px;
`;

const MobileContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 18px;
`;

const TopInfo = () => (
  <Flex justifyContent="space-between" alignItems="flex-end" mb="60px">
    <Logo src={logoSrc} />
    <Text fontSize={2} lineHeight={1}>
      <Link to={"/privacy-policy"}>Privacy Policy</Link>
    </Text>
  </Flex>
);

type ResetPasswordForm = {
  password: string;
  confirmPassword: string;
};

const ResetPassword = (): JSX.Element => {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const { resetPassword } = useUser();
  const { register, handleSubmit, errors } = useForm<ResetPasswordForm>();
  const history = useHistory();

  const onSubmit = async (data: ResetPasswordForm) => {
    const { password } = data;

    setLoading(true);
    const success = await resetPassword(code, password);
    setLoading(false);

    if (success) {
      history.push("/");
    } else {
      window.alert("An error occurred");
    }
  };
  return (
    <Flex flexDirection="column" flex="1" p={3}>
      <Text fontWeight="600" fontSize={[4, 5]} mb={4} color="black">
        Enter your new, secure <br />
        password below.
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          name="password"
          type="password"
          label="Password"
          error={errors.password}
          ref={register({
            required: true,
            minLength: 8,
          })}
          placeholder="Your password..."
        />
        <Button type="submit" mr={1}>
          Reset Password
        </Button>
        <Button to="/login" hollow>
          Login
        </Button>
      </form>
      {loading && <Loader overlay />}
    </Flex>
  );
};

type ForgotPasswordForm = {
  email: string;
};

const ForgotPassword = (): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, errors } = useForm<ForgotPasswordForm>();

  const onSubmit = (data: ForgotPasswordForm) => {
    const { email } = data;

    setLoading(true);
    axios
      .post("/api/auth/forgot-password", { email })
      .then(({ data }) => {
        setSent(true);
      })
      .catch((err) => {
        window.alert("An error occurred");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Flex flexDirection="column" flex="1" p={3}>
      {sent ? (
        <Text fontWeight="600" fontSize={[4, 5]} mb={4} color="black">
          Check your email for a link <br />
          to reset your password.
        </Text>
      ) : (
        <>
          <Text fontWeight="600" fontSize={[4, 5]} mb={4} color="black">
            For your password? <br />
            Enter your email below.
          </Text>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormInput
              name="email"
              label="Email"
              error={errors.email}
              ref={register({
                required: true,
              })}
              placeholder="Your email address..."
              autoFocus
            />
            <Button type="submit" mr={1}>
              Send Reset Email
            </Button>
            <Button to="/login" hollow>
              Login
            </Button>
          </form>
        </>
      )}
      {loading && <Loader overlay />}
    </Flex>
  );
};

type LoginFormType = {
  email: string;
  password: string;
};

const LoginForm = ({ active }: { active: boolean }): JSX.Element => {
  const { login } = useUser();
  const [loading, setLoading] = useState<boolean>(false);
  const { register, handleSubmit, errors } = useForm<LoginFormType>();
  const history = useHistory();

  const onSubmit = async (data: LoginFormType) => {
    setLoading(true);
    const success = await login(data);
    setLoading(false);
    if (success) {
      history.push("/");
    } else {
      window.alert("Invalid Email/Password");
    }
  };

  return (
    <Flex flexDirection="column" flex="1" p={3}>
      <Text fontWeight="600" fontSize={[4, 5]} mb={4} color="black">
        Welcome Back, <br />
        Let's get you logged in.
      </Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          name="email"
          label="Email"
          error={errors.email}
          ref={register({
            required: true,
          })}
          placeholder="Your email address..."
          tabindex={active ? 0 : -1}
        />
        <FormInput
          name="password"
          type="password"
          error={errors.password}
          label="Password"
          tabindex={active ? 0 : -1}
          renderLabel={(props) => (
            <Flex justifyContent="space-between">
              <Label htmlFor="password">{props.label}</Label>
              <Text fontSize={1}>
                <Link tabIndex={active ? 2 : -1} to="/forgot-password">
                  Forgot?
                </Link>
              </Text>
            </Flex>
          )}
          ref={register({
            required: true,
          })}
          mb={5}
          placeholder="Your password..."
        />
        <Button type="submit" mr={1}>
          Log In
        </Button>
        <Button
          onClick={() => {
            window.location.href = "https://www.aikahq.com/sign-up";
          }}
          type="button"
          hollow
        >
          Sign Up
        </Button>
      </form>
      {loading && <Loader overlay />}
    </Flex>
  );
};

type SignUpFormType = {
  firstName: string;
  lastName: string;
  email: string;
  newpassword: string;
  allowEmails: boolean;
};

const SignUpForm = ({ active }: { active: boolean }) => {
  const { signup } = useUser();
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, control, errors } = useForm<SignUpFormType>();

  const history = useHistory();

  const onSubmit = async (data: SignUpFormType) => {
    const { email, newpassword, allowEmails } = data;

    const spec = {
      email: email.trim().toLowerCase(),
      password: newpassword,
      firstName: data.firstName,
      lastName: data.lastName,
      allowEmails: !!allowEmails,
    };

    setLoading(true);
    const success = await signup(spec);
    setLoading(false);
    if (success) {
      history.push("/");
    } else {
      window.alert("Email is taken");
    }
  };

  return (
    <Flex flexDirection="column" flex="1" p={3} pt={0}>
      <Text fontWeight="600" fontSize={[4, 5]} mb={4} color="black">
        Seconds away from <br />a more productive life.
      </Text>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id="signup-form"
        autoComplete="off"
      >
        <Flex alignItems="center">
          <FormInput
            name="firstName"
            label="First Name"
            error={errors.firstName}
            data-lpignore="true"
            ref={register({
              required: true,
            })}
            placeholder="Your first name..."
            tabindex={active ? 0 : -1}
            mr={1}
          />
          <FormInput
            ml={1}
            name="lastName"
            label="Last Name"
            error={errors.lastName}
            ref={register}
            placeholder="Your last name..."
            tabindex={active ? 0 : -1}
          />
        </Flex>

        <FormInput
          name="email"
          label="Email"
          error={errors.email}
          type="email"
          ref={register({
            required: true,
          })}
          placeholder="Your email address..."
          tabindex={active ? 0 : -1}
        />
        <FormInput
          name="password"
          label="Choose a Password"
          type="password"
          autoComplete="new-password"
          error={errors.newpassword}
          ref={register({
            required: true,
            minLength: 8,
          })}
          tabindex={active ? 0 : -1}
        />
        <Controller
          name="allowEmails"
          defaultValue={false}
          control={control}
          render={({ ref, ...props }) => (
            <CheckBox
              {...props}
              label={
                <Text fontWeight={600} fontSize={2}>
                  Include a FREE subscription to the{" "}
                  <a
                    href="https://www.bebrisk.email"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Brisk
                  </a>{" "}
                  newsletter for producitivty tips from our founder.
                </Text>
              }
              mb={5}
            />
          )}
        />
        <Button type="submit" mr={1}>
          Create Account
        </Button>
        <Button to="/login" hollow>
          Log In
        </Button>
      </form>
      {loading && <Loader overlay />}
    </Flex>
  );
};

const LoginPage = () => {
  const { user } = useUser();

  if (user != null) {
    return <Redirect to="/" />;
  }

  return (
    <Container>
      <LoginPanel p={0}>
        <Image
          src={rightGfx}
          style={{
            left: 400,
            right: 0,
            width: "auto",
            backgroundPosition: "top right",
          }}
        />
        <InfoPanel left>
          <TopInfo />
          <Route
            path={["/login", "/signup"]}
            render={() => <LoginForm active />}
          />
          <Route
            path={["/forgot-password"]}
            render={() => <ForgotPassword />}
          />
          <Route
            path={"/reset-password/:code"}
            render={() => <ResetPassword />}
          />
        </InfoPanel>
      </LoginPanel>
    </Container>
  );
};

const MobileLoginPage = () => {
  return (
    <MobileContainer>
      <TopInfo />
      <Switch>
        <Route path="/forgot-password">
          <ForgotPassword />
        </Route>
        <Route path="/reset-password/:code">
          <ResetPassword />
        </Route>
        <Route path="/login">
          <LoginForm active />
        </Route>
        <Route path="/signup">
          <SignUpForm active />
        </Route>
      </Switch>
    </MobileContainer>
  );
};

const LoginPageSwitcher = () => {
  const isMobile = /Mobi/.test(window.navigator.userAgent);

  return isMobile ? <MobileLoginPage /> : <LoginPage />;
};

export default LoginPageSwitcher;
